import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { decode } from 'jsonwebtoken-esm';
import { Emitter } from '@leyan/emitter';
import createNamedContext from 'utils/createNamedContext';
import useTrackingRef from 'hooks/useTrackingRef';
import { simpleParse } from 'components/RouterQuery';
import refine, { Refinable } from 'utils/refine';
import AppLoading from 'components/AppLoading';
import { init as initTracker } from 'utils/tracker';
import chatttSDK from 'services/chattt';
import { setAccid } from 'components/Nim/nimReport';
import { clear, isChangePassword } from 'utils/nimKick';
import { loggedUserCache } from 'services/cache';
import { UserDB } from 'lib/db';
import { atom, useAtom, useAtomValue } from 'jotai';
import nimInst from 'services/nim';

export const payloadAtom = atom<Oversea.Token.Payload | null>(null);

export const usePayload = () => useAtomValue(payloadAtom);

/**
 * 取消授权操作码
 */
export type UnauthorizeCode =
  | /* 授权码移除 */ 'TOKEN_REMOVE'
  | /* 授权码禁用 */ 'TOKEN_FORBIDDEN'
  | /* 授权码缺失 */ 'TOKEN_MISSING'
  | /* 授权码非法 */ 'TOKEN_INVALID'
  | /* 授权码过期 */ 'TOKEN_EXPIRED';

/**
 * 事件取消授权操作码
 */
export type EventsUnauthorizeCode =
  | /* 授权码移除 */ '@EVENT_TOKEN_REMOVE'
  | /* 授权码禁用 */ '@EVENT_TOKEN_FORBIDDEN';

/**
 * 授权事件
 */
export type AuthorizationEvents<T = unknown> = {
  /**
   * 授权事件
   * @param token 授权 token
   * @param payload 授权信息
   */
  authorize: (token: string, payload: T, apiAuthorize?: boolean) => void;
  /**
   * 取消授权
   * @param code 操作码
   * @param reason 原因
   */
  unauthorize: (code: EventsUnauthorizeCode | UnauthorizeCode, reason: string) => void;
};

/**
 * 授权事件
 */
export const authorizationEvents = new Emitter<AuthorizationEvents>();

/**
 * 取消授权
 * @param code 操作码
 * @param reason 原因
 */
export function unauthorize(code: EventsUnauthorizeCode, reason: string) {
  authorizationEvents.emit('unauthorize', code, reason);
}

/**
 * 监听授权事件
 * @param handle 授权事件处理函数
 * @returns 取消监听事件
 */
export function onAuthorize<T = unknown>(
  handle: (token: string, payload: T, apiAuthorize?: boolean) => void,
) {
  return authorizationEvents.on('authorize', (token, payload, apiAuthorize) => {
    handle(token, payload as T, apiAuthorize);
  });
}

/**
 * 监听取消授权事件
 * @param handle 取消授权事件处理函数
 * @returns 取消监听事件
 */
export function onUnauthorize(handle: (code: UnauthorizeCode, reason: string) => void) {
  return authorizationEvents.on('unauthorize', (code, reason) => {
    if (code !== '@EVENT_TOKEN_REMOVE' && code !== '@EVENT_TOKEN_FORBIDDEN') {
      handle(code, reason);
    }
  });
}

/**
 * 设置客户端当前用户信息
 * @param users
 */
export function setChatUsers(users: Chattt.SessionUser) {
  if (chatttSDK?.app?.setUsers) {
    chatttSDK.app.setUsers(users);
  }
}

/**
 * 授权 hook 选项
 */
export interface AuthorizationHookOptions {
  /**
   * 存储对象
   */
  storage?: Storage;
  /**
   * 缓存键名
   */
  cacheKey?: string;
  /**
   * query 键名
   */
  queryKey?: string;
}

/**
 * 授权 hook aoi
 */
export interface AuthorizationHookApi<T = unknown> {
  /**
   * 授权中
   */
  loading: boolean;
  /**
   * 是否已经授权
   */
  authorized: boolean;
  /**
   * 授权 token
   */
  token: string | null;
  /**
   * 授权信息
   */
  payload: T | null;
  /**
   * 授权 token 来源
   */
  tokenSource: 'state' | 'query';

  /**
   * 授权
   * @param token 授权 token
   */
  authorize(token: string): void;

  /**
   * 取消授权
   * @param code 操作码
   * @param reason 原因
   */
  unauthorize(code: UnauthorizeCode, reason: string): void;
}

/**
 * 授权 api hook
 * @param options 授权 api hook 选项
 */
export function useAuthorizationApi<T = unknown>(options: AuthorizationHookOptions = {}) {
  const {
    storage = sessionStorage,
    cacheKey = '@authorization-token',
    queryKey = 'token',
  } = options;

  const [query, setQuery] = useState<any>(() => {
    const search = window.location.hash?.split('?')?.[1];
    return simpleParse(search);
  });
  useEffect(() => {
    const handleHashChange = () => {
      const search = window.location.hash?.split('?')?.[1];
      setQuery(simpleParse(search));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [stateToken, setStateToken] = useState(() => {
    return isChangePassword() ? '' : storage.getItem(cacheKey);
  });

  const [payload, setPayload] = useAtom(payloadAtom);
  const { token, tokenFrom } = useMemo(() => {
    const { [queryKey]: queryToken } = query;

    return typeof queryToken === 'string'
      ? {
          token: queryToken,
          tokenFrom: 'query',
        }
      : {
          token: stateToken,
          tokenFrom: 'state',
        };
  }, [query, queryKey, stateToken]);

  const internalApiRef = useTrackingRef({
    isTokenAuthorized(token: string) {
      return authorized && stateToken === token;
    },
    authorize(token: string, payload: T) {
      storage.setItem(cacheKey, token);
      // @ts-ignore
      loggedUserCache.set(payload?.name || payload?.account);
      authorizationEvents.emit('authorize', token, payload);
      // @ts-ignore
      if (payload?.id) {
        // @ts-ignore
        UserDB.init(payload.id);
      }
    },
    unauthorize(code: UnauthorizeCode, reason: string) {
      storage.removeItem(cacheKey);
      loggedUserCache.clear();
      authorizationEvents.emit('unauthorize', code, reason);
      UserDB.destroy();
      // token 失效/被移除时，主动销毁 IM 实例与心跳（避免后台 worker/连接残留）
      nimInst.logout().catch(() => {});
    },
  });

  const unauthorize = useCallback(
    (code: UnauthorizeCode, reason: string) => {
      internalApiRef.current.unauthorize(code, reason);

      batchedUpdates(() => {
        setStateToken(null);
        setLoading(false);
        setAuthorized(false);
        setPayload(null);
      });
    },
    [internalApiRef, setPayload],
  );
  const authorize = useCallback(
    (token: string | null) => {
      if (token === null) {
        unauthorize('TOKEN_MISSING', 'token not provided');

        return;
      }

      if (internalApiRef.current.isTokenAuthorized(token)) {
        return;
      }

      const payload = decode(token, { json: true }) as T;

      if (payload === null) {
        unauthorize('TOKEN_INVALID', `token "${token}" invalid`);

        return;
      }

      if (typeof (payload as any).exp === 'number' && (payload as any).exp * 1000 < Date.now()) {
        unauthorize('TOKEN_EXPIRED', `token "${token}" expired`);

        return;
      }

      clear();

      internalApiRef.current.authorize(token, payload);

      batchedUpdates(() => {
        setStateToken(token);
        setLoading(false);
        setAuthorized(true);
        setPayload(payload as T);
      });
      initTracker(payload);
      setChatUsers({
        orgId: (payload as any)?.orgId,
        userId: (payload as any)?.id,
        userName: (payload as any)?.name,
        token,
      });
      setAccid((payload as any)?.accid);
    },
    [internalApiRef, setPayload, unauthorize],
  );

  useEffect(() => {
    authorize(token);
  }, [authorize, token]);

  return useMemo(
    () =>
      ({
        loading,
        authorized,
        token,
        payload,
        tokenSource: tokenFrom,
        authorize,
        unauthorize,
      } as AuthorizationHookApi<T>),
    [loading, authorized, token, payload, tokenFrom, authorize, unauthorize],
  );
}

/**
 * 授权 api context
 */
export const AuthorizationApiContext = createNamedContext<AuthorizationHookApi | null>(
  'AuthorizationStateContext',
  null,
);

/**
 * 获取授权 api
 */
export function useAuthorization<T = unknown>() {
  const api = useContext(AuthorizationApiContext);

  if (api === null) {
    throw new Error(
      'useAuthorization() may be used only in the context of a <Authorization> component.',
    );
  }

  return api as AuthorizationHookApi<T>;
}

const { Provider: AuthorizationApiProvider } = AuthorizationApiContext;

/**
 * 授权容器属性
 */
export interface AuthorizationProps<T = unknown> extends AuthorizationHookOptions {
  /**
   * 授权中替换元素
   */
  fallback?: Refinable<() => React.ReactNode>;

  /**
   * 授权事件处理函数
   * @param token 授权 token
   * @param payload 授权信息
   */
  onAuthorize?(token: string, payload: T): void;

  /**
   * 取消授权事件处理函数
   * @param code 操作码
   * @param reason 原因
   */
  onUnauthorize?(code: UnauthorizeCode, reason: string): void;

  /**
   * 子元素
   */
  children?: Refinable<(api: AuthorizationHookApi) => React.ReactNode>;
}

/**
 * 授权容器
 * @param props 授权容器属性
 */
function Authorization<T = unknown>(props: AuthorizationProps<T>) {
  const {
    fallback = <AppLoading />,
    onAuthorize,
    onUnauthorize,
    storage,
    cacheKey,
    queryKey,
    children,
  } = props;

  const api = useAuthorizationApi({
    storage,
    cacheKey,
    queryKey,
  });
  const parentApiValue = useContext(AuthorizationApiContext);
  const apiValue = useMemo(() => {
    return (
      parentApiValue === null ? api : parentApiValue
    ) as AuthorizationHookApi<Oversea.Token.Payload>;
  }, [api, parentApiValue]);

  useLayoutEffect(() => {
    const offAuthorize = authorizationEvents.on('authorize', (token, payload, apiAuthorize) => {
      if (onAuthorize) {
        onAuthorize(token, payload as T);
      }
      if (apiAuthorize) {
        api.authorize(token);
      }
    });

    const offUnauthorize = authorizationEvents.on('unauthorize', (code, reason) => {
      if (code === '@EVENT_TOKEN_REMOVE') {
        api.unauthorize('TOKEN_REMOVE', reason);
      } else if (code === '@EVENT_TOKEN_FORBIDDEN') {
        api.unauthorize('TOKEN_FORBIDDEN', reason);
      } else if (onUnauthorize) {
        onUnauthorize(code, reason);
      }
    });

    return () => {
      offAuthorize();
      offUnauthorize();
    };
  }, [api, onAuthorize, onUnauthorize]);

  if (api.loading) {
    return <>{refine(fallback)}</>;
  }

  return (
    <AuthorizationApiProvider value={apiValue}>
      {refine(children, apiValue)}
    </AuthorizationApiProvider>
  );
}

export default Authorization;
